import { Injectable, NgZone, inject } from '@angular/core';
import moment from 'moment';
import {
  GetBatchJobsGQL,
  GetBatchJobsQuery,
  GetBatchJobsQueryVariables,
  GetContactListQueryVariables,
} from '@graphql';
import { FileDownloadService } from './file-download-service';
import { QueryRef } from 'apollo-angular';
import { Observable, of, throwError, timer, shareReplay, race, fromEvent, merge } from 'rxjs';
import { BatchJobsList } from '../../shared/models/batch-jobs-list';
import { map, catchError, filter, take, retry } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BatchJob, JobDetails } from '@shared/models/batch-job';
import { TokenService } from '@cm/ui-modules';
import { bearerSseAuth, fetchEventStream, isSseAuthError } from '../../shared/services/sse-fetch';

/** One job entry pushed by the Java SSE stream (/api/core/batchjobs/stream). */
export interface BatchJobStreamJob {
  id: number;
  uuid: string;
  name: string;
  type: string;
  progress: number;
  working: boolean;
  details: string | null;
  startedAt: string;
  lastUpdatedAt: string | null;
  startedById: number | null;
  logAvailable?: boolean;
  logDownloadUrl?: string;
}

export interface BatchJobStreamEvent {
  jobs: BatchJobStreamJob[];
}

@Injectable({
  providedIn: 'root',
})
export class BatchJobService {
  private readonly getBatchJobsGQL = inject(GetBatchJobsGQL);
  private readonly translateService = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly ngZone = inject(NgZone);
  private readonly tokenService = inject(TokenService);

  batchJobListQuery!: QueryRef<GetBatchJobsQuery, GetBatchJobsQueryVariables>;

  /**
   * Central SSE stream for all batch-job progress updates.
   * Shared across all subscribers — one connection per session.
   * Persists across component lifecycles (refCount: false) and
   * replays the last event to new subscribers.
   */
  readonly stream$: Observable<BatchJobStreamEvent>;

  constructor() {
    this.stream$ = this.createStream().pipe(
      retry({
        // Backoff restarts from 1 s after every successful connection.
        resetOnSuccess: true,
        delay: (error, retryCount) => {
          // A dead session cannot be cured by reconnecting; the auth guard sends the user to login.
          if (isSseAuthError(error)) return throwError(() => error);
          console.error('[BatchJobService] SSE stream dropped, reconnecting:', error);
          // Back-online / tab-visible skips the remaining wait.
          return race(
            timer(Math.min(30000, 1000 * 2 ** retryCount)),
            merge(
              fromEvent(window, 'online'),
              fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden)),
            ),
          ).pipe(take(1));
        },
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  /** Batch jobs now come from the Java backend only (GraphQL). */
  getList(variables: GetContactListQueryVariables): Observable<BatchJobsList> {
    const watchVars = {
      offset: variables.offset ?? 0,
      limit: variables.limit ?? 25,
      orderBy: variables.orderBy ?? {},
    };
    if (!this.batchJobListQuery) {
      this.batchJobListQuery = this.getBatchJobsGQL.watch({
        variables: watchVars,
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      } as any);
    } else {
      this.batchJobListQuery.refetch(watchVars as any);
    }
    // valueChanges is a hot, long-lived stream (it keeps emitting on every cache
    // update) — take(1) makes getList() behave like a one-shot request so
    // finalize()/loading flags in callers resolve; live updates come from the
    // SSE stream instead, not from resubscribing to this observable.
    return this.batchJobListQuery.valueChanges.pipe(
      filter((result) => !result.loading),
      take(1),
      map((result) => {
        if (result.error) {
          throw result.error;
        }
        return responseToBatchJobList(result, this.translateService);
      }),
      catchError((error) => {
        console.error('GraphQL batch jobs error:', error);
        return of({
          total: 0,
          rows: [],
          error: true,
        });
      }),
    );
  }

  private createStream(): Observable<BatchJobStreamEvent> {
    return new Observable<BatchJobStreamEvent>((subscriber) => {
      const handle = fetchEventStream(
        '/api/core/batchjobs/stream',
        bearerSseAuth(this.tokenService),
        (data) => {
          this.ngZone.run(() => {
            try {
              subscriber.next(JSON.parse(data) as BatchJobStreamEvent);
            } catch (e) {
              console.error('[BatchJobService] Error parsing SSE event:', e);
            }
          });
        },
        (error) => this.ngZone.run(() => subscriber.error(error)),
        // A clean close (proxy idle timeout, server restart) is a drop, not completion: reconnect.
        () => this.ngZone.run(() => subscriber.error(new Error('SSE stream closed by server'))),
      );

      return () => handle.close();
    });
  }

  private computeStatus(working: boolean, progress: number): string {
    if (working) {
      return progress
        ? this.translateService.instant('API.BatchJob.status.in_progress')
        : this.translateService.instant('API.BatchJob.status.waiting');
    }
    return progress >= 100
      ? this.translateService.instant('API.BatchJob.status.completed')
      : this.translateService.instant('API.BatchJob.status.failed');
  }

  private parseDetails(details: string | null | undefined, fallback: JobDetails = {}): JobDetails {
    if (!details) {
      return fallback;
    }
    try {
      return JSON.parse(details);
    } catch (e) {
      return fallback;
    }
  }

  /** Maps an SSE job entry onto an existing table row. */
  applyStreamJob(row: BatchJob, streamJob: BatchJobStreamJob): BatchJob {
    const parsedDetails = this.parseDetails(streamJob.details, row.parsedDetails ?? {});
    return {
      ...row,
      progress: streamJob.progress ?? row.progress,
      working: streamJob.working,
      parsedDetails,
      message: (parsedDetails as any)?.message || row.message,
      status: this.computeStatus(streamJob.working, streamJob.progress ?? row.progress),
      errorCount: (parsedDetails as any)?.errors || row.errorCount || 0,
      lastUpdatedAt: streamJob.lastUpdatedAt
        ? moment.utc(streamJob.lastUpdatedAt).local().toDate()
        : row.lastUpdatedAt,
      logAvailable: streamJob.logAvailable ?? row.logAvailable,
      logDownloadUrl: streamJob.logDownloadUrl ?? row.logDownloadUrl,
    } as BatchJob;
  }

  /** Builds a full table row from an SSE job entry that hasn't been seen via GraphQL yet. */
  toNewRow(streamJob: BatchJobStreamJob): BatchJob {
    const parsedDetails = this.parseDetails(streamJob.details);
    return {
      id: streamJob.id,
      uuid: streamJob.uuid || '',
      details: streamJob.details || '',
      parsedDetails,
      fileName: '',
      instanceSynchronizable: false,
      lastUpdatedAt: streamJob.lastUpdatedAt
        ? moment.utc(streamJob.lastUpdatedAt).local().toDate()
        : new Date(),
      name: this.translateService.instant(streamJob.name || ''),
      message: (parsedDetails as any)?.message || '',
      progress: streamJob.progress || 0,
      startedAt: moment.utc(streamJob.startedAt).local().toDate(),
      startedById: streamJob.startedById || 0,
      type: streamJob.type || '',
      userName: '',
      working: streamJob.working,
      status: this.computeStatus(streamJob.working, streamJob.progress || 0),
      errorCount: (parsedDetails as any)?.errors || 0,
      source: 'graphql',
      logAvailable: streamJob.logAvailable,
      logDownloadUrl: streamJob.logDownloadUrl,
    } as BatchJob;
  }

  queryRows(variables: GetBatchJobsQueryVariables): void {
    this.batchJobListQuery
      .fetchMore({
        variables: variables as any,
      })
      .then((fetchMoredResult) => {
        return this.batchJobListQuery.updateQuery((prev) => fetchMoredResult.data || prev);
      });
  }

  downloadImportFile(job: BatchJob): Observable<Blob | ArrayBuffer> | undefined {
    if (!job.id) {
      return throwError(() => new Error('Failed to download file'));
    }
    const downloadObservable = this.fileDownloadService.downloadFile({ fileName: job.id });
    if (!downloadObservable) {
      return throwError(() => new Error('Failed to download file'));
    }

    return downloadObservable.pipe(
      map((response: HttpResponse<ArrayBuffer>) => {
        if (!response.body) {
          throw new Error('Empty response body');
        }
        return response.body;
      }),
      catchError((error) => throwError(() => new Error('Failed to download file'))),
    );
  }

  downloadErrorFile(uuid: string): Observable<Blob> {
    return this.http.get(`/api/core/contact/import/failed/${uuid}`, {
      responseType: 'blob',
    });
  }

  downloadLogFile(uuid: string): Observable<Blob> {
    return this.http.get(`/api/core/contact/import/log/${uuid}`, {
      responseType: 'blob',
    });
  }
}

function computeStatus(
  working: boolean,
  progress: number,
  translateService: TranslateService,
): string {
  if (working) {
    return progress
      ? translateService.instant('API.BatchJob.status.in_progress')
      : translateService.instant('API.BatchJob.status.waiting');
  }
  return progress >= 100
    ? translateService.instant('API.BatchJob.status.completed')
    : translateService.instant('API.BatchJob.status.failed');
}

function responseToBatchJobList(response: any, translateService: TranslateService): BatchJobsList {
  if (!response.data) {
    return {
      error: true,
      rows: [],
      total: 0,
    };
  }
  const batchJobList = response.data.getBatchJobList;
  return {
    total: batchJobList?.total || 0,
    rows:
      batchJobList?.data?.map((row: any) => {
        let parsedDetails: JobDetails = {};
        try {
          if (row.details && row.details !== '') {
            parsedDetails = JSON.parse(
              typeof row.details === 'string' ? row.details : JSON.stringify(row.details),
            );
          }
        } catch (e) {
          console.warn('Failed to parse job details:', e);
        }

        return {
          id: row.id,
          uuid: row.uuid || '',
          details: row.details || '',
          parsedDetails,
          fileName: '', // GraphQL schema doesn't include fileName
          instanceSynchronizable: row.instanceSynchronizable || false,
          lastUpdatedAt: moment.utc(row.lastUpdatedAt).local().toDate(),
          name: translateService.instant(row.name || ''),
          message: (parsedDetails as any)?.message || '',
          progress: row.progress || 0,
          startedAt: moment.utc(row.startedAt).local().toDate(),
          startedById: row.startedById || 0,
          type: row.type || '',
          userName: row.userName || '',
          working: row.working || false,
          status: computeStatus(row.working, row.progress, translateService),
          errorCount: parsedDetails?.errors || 0,
          source: 'graphql' as const,
          logAvailable: row.logAvailable || false,
          logDownloadUrl: row.logDownloadUrl || undefined,
        };
      }) || [],
  };
}
